import { Component } from '@theme/component';

/**
 * Mobile-only product carousel for homepage hero products.
 * Desktop keeps the CSS grid; ≤749px uses scroll-snap + dot pagination.
 *
 * @extends {Component}
 */
class HomepageHeroProducts extends Component {
  /** @type {MediaQueryList | undefined} */
  #mobileMq;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} */
  #dots = null;

  /** @type {boolean} */
  #scrollPending = false;

  /** @type {number} */
  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#scroller = this.querySelector('[data-product-scroller]');
    this.#dots = this.querySelector('[data-product-dots]');
    this.#mobileMq = window.matchMedia('(max-width: 749px)');
    this.#mobileMq.addEventListener('change', this.#onViewportChange);

    this.#setupForViewport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownMobile();
    this.#mobileMq?.removeEventListener('change', this.#onViewportChange);
  }

  #onViewportChange = () => {
    this.#setupForViewport();
  };

  #isMobile() {
    return this.#mobileMq?.matches ?? window.matchMedia('(max-width: 749px)').matches;
  }

  #setupForViewport() {
    this.#teardownMobile();

    if (!this.#isMobile()) {
      this.dataset.mode = 'grid';
      return;
    }

    this.dataset.mode = 'carousel';
    this.#bindMobile();
  }

  #bindMobile() {
    const scroller = this.#scroller;
    const dots = this.#dots;
    if (!(scroller instanceof HTMLElement) || !(dots instanceof HTMLElement)) return;

    const slides = [...scroller.children].filter((el) => el instanceof HTMLElement);
    if (slides.length <= 1) {
      dots.hidden = true;
      return;
    }

    dots.hidden = false;
    dots.replaceChildren();

    for (const [index, slide] of slides.entries()) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'homepage-hero-products__dot';
      button.setAttribute('aria-label', `Product ${index + 1} of ${slides.length}`);
      button.dataset.slideIndex = String(index);
      if (index === 0) button.setAttribute('aria-current', 'true');
      dots.appendChild(button);
    }

    scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    dots.addEventListener('click', this.#onDotsClick);
    this.#syncDots(0);
  }

  #teardownMobile() {
    this.#scroller?.removeEventListener('scroll', this.#onScroll);
    this.#dots?.removeEventListener('click', this.#onDotsClick);

    if (this.#dots instanceof HTMLElement) {
      this.#dots.replaceChildren();
      this.#dots.hidden = true;
    }

    this.#index = 0;
    this.#scrollPending = false;
  }

  #onScroll = () => {
    if (this.#scrollPending) return;

    this.#scrollPending = true;
    requestAnimationFrame(() => {
      this.#scrollPending = false;
      this.#syncFromScroll();
    });
  };

  /**
   * @param {MouseEvent} event
   */
  #onDotsClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-slide-index]');
    if (!(button instanceof HTMLElement)) return;

    const index = Number(button.dataset.slideIndex);
    if (Number.isNaN(index)) return;

    this.#goTo(index);
  };

  #syncFromScroll() {
    const scroller = this.#scroller;
    if (!(scroller instanceof HTMLElement)) return;

    const width = scroller.clientWidth;
    if (width <= 0) return;

    const index = Math.round(scroller.scrollLeft / width);
    this.#syncDots(index);
  }

  /**
   * @param {number} index
   */
  #goTo(index) {
    const scroller = this.#scroller;
    if (!(scroller instanceof HTMLElement)) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollTo({
      left: index * scroller.clientWidth,
      behavior: reduced ? 'auto' : 'smooth',
    });
    this.#syncDots(index);
  }

  /**
   * @param {number} index
   */
  #syncDots(index) {
    this.#index = index;
    const dots = this.#dots?.querySelectorAll('[data-slide-index]');
    if (!dots) return;

    for (const [i, dot] of dots.entries()) {
      if (!(dot instanceof HTMLElement)) continue;
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('homepage-hero-products')) {
  customElements.define('homepage-hero-products', HomepageHeroProducts);
}
